import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'https://adamsched-eu.onrender.com/api';

// ── Helpers ──────────────────────────────────────────────────────────────────

const DAYS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTHS_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function toUTCDateString(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function startOfWeekMonday(date) {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

function formatDateLabel(date) {
  return `${DAYS_ES[date.getUTCDay()]} ${date.getUTCDate()} de ${MONTHS_ES[date.getUTCMonth()]}`;
}

function formatTimeLocal(isoStr, timezone) {
  try {
    return new Date(isoStr).toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit', timeZone: timezone,
    });
  } catch {
    return isoStr.slice(11, 16);
  }
}

function isoToLocalDate(isoStr, timezone) {
  try {
    const d = new Date(isoStr);
    return new Date(d.toLocaleString('en-US', { timeZone: timezone }));
  } catch {
    return new Date(isoStr);
  }
}

// Build available slots for a day from weekly template + bookings
function buildSlotsForDay(dayKey, weeklyTemplate, bookings, capacityPerSlot, timezone, date) {
  if (!weeklyTemplate) return [];
  const daySlots = weeklyTemplate.filter(s => s.day === dayKey);
  const now = new Date();

  return daySlots.flatMap(slot => {
    const [h, m] = (slot.startTime || '').split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return [];

    const localDateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`;
    const slotLocal = new Date(localDateStr);
    if (slotLocal <= now) return [];
    const isoStr = slotLocal.toISOString().replace(/\.\d{3}Z$/, '.000Z');
    const timeLabel = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
    const booked = bookings.filter(b => b.startDateTime === isoStr).length;
    return [{
      startDateTime: isoStr,
      timeLabel,
      booked,
      available: booked < capacityPerSlot,
      capacity: capacityPerSlot,
    }];
  }).sort((a, b) => a.startDateTime.localeCompare(b.startDateTime));
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// ── Spinner ───────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-8 h-8 border-4 border-zinc-300 border-t-zinc-800 rounded-full animate-spin" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BookingWidget() {
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [centerData, setCenterData] = useState(null);   // { center, calendars }

  const [step, setStep] = useState(1);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Availability
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);

  // Mobile: day navigation within the week
  const [mobileDayIndex, setMobileDayIndex] = useState(0);

  // Form
  const [form, setForm] = useState({ fullName: '', surname: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Confirmation
  const [confirmation, setConfirmation] = useState(null);

  // ── Fetch center ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/centers/${slug}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'Centro no encontrado.' : 'Error al cargar el centro.');
          return;
        }
        const data = await res.json();
        setCenterData(data);
        if (data.calendars?.length === 1) {
          setSelectedCalendar(data.calendars[0]);
        }
      } catch {
        setError('Error de conexión. Inténtalo de nuevo.');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // ── Fetch availability when calendar or week changes ────────────────────────
  const fetchAvailability = useCallback(async (calendar, wStart) => {
    if (!calendar || !centerData) return;
    setLoadingAvail(true);
    try {
      const weekStr = toUTCDateString(wStart);
      const res = await fetch(
        `${API_BASE}/public/availability/${slug}?calendarId=${calendar.id}&weekStart=${weekStr}`
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAvailability(data);
    } catch {
      setAvailability(null);
    } finally {
      setLoadingAvail(false);
    }
  }, [slug, centerData]);

  useEffect(() => {
    if (selectedCalendar) fetchAvailability(selectedCalendar, weekStart);
  }, [selectedCalendar, weekStart, fetchAvailability]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleCalendarSelect = (cal) => {
    setSelectedCalendar(cal);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    if (!slot.available) return;
    setSelectedSlot(slot);
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevWeek = () => {
    const prev = addDays(weekStart, -7);
    const today = startOfWeekMonday(new Date());
    if (prev >= today) {
      setWeekStart(prev);
      setMobileDayIndex(0);
    }
  };

  const handleNextWeek = () => {
    if (!availability) return;
    const next = addDays(weekStart, 7);
    const maxWeek = startOfWeekMonday(
      addDays(new Date(), (availability.bookingWindowWeeks || 4) * 7)
    );
    if (next <= maxWeek) {
      setWeekStart(next);
      setMobileDayIndex(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim() || !selectedSlot || !selectedCalendar) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/public/bookings/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          calendarId: selectedCalendar.id,
          startDateTime: selectedSlot.startDateTime,
          fullName: form.fullName.trim(),
          surname: form.surname.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
        }),
      });
      if (res.status === 409) {
        setSubmitError('Este horario ya no está disponible. Por favor, elige otro.');
        setStep(1);
        setSelectedSlot(null);
        fetchAvailability(selectedCalendar, weekStart);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setSubmitError(body.detail || 'Error al crear la reserva. Inténtalo de nuevo.');
        return;
      }
      const data = await res.json();
      setConfirmation({ ...data, email: form.email.trim() });
      setStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────────

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const timezone = availability?.timezone || centerData?.center?.timezone || 'Europe/Madrid';
  const formValid = form.fullName.trim() && form.email.trim();

  // ── Loading / error states ───────────────────────────────────────────────────

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Spinner />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <p className="text-lg font-semibold text-zinc-800 mb-2">No disponible</p>
        <p className="text-zinc-500">{error}</p>
      </div>
    </div>
  );

  const { center, calendars } = centerData;

  // ── Step 3: Confirmation ─────────────────────────────────────────────────────

  if (step === 3 && confirmation) {
    const localDate = isoToLocalDate(confirmation.startDateTime, timezone);
    const confirmDate = `${DAYS_ES[localDate.getDay()]} ${localDate.getDate()} de ${MONTHS_ES[localDate.getMonth()]}`;
    const confirmTime = formatTimeLocal(confirmation.startDateTime, timezone);
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">¡Reserva confirmada!</h1>
            <p className="text-zinc-500 mt-1 text-sm">Recibirás un email de confirmación en {confirmation.email}</p>
          </div>
          <div className="bg-zinc-50 rounded-xl p-5 text-left space-y-2 border border-zinc-200">
            <p className="text-sm text-zinc-500">Centro</p>
            <p className="font-semibold text-zinc-900">{confirmation.centerName}</p>
            <p className="text-sm text-zinc-500 mt-3">Servicio</p>
            <p className="font-semibold text-zinc-900">{confirmation.calendarName}</p>
            <p className="text-sm text-zinc-500 mt-3">Fecha y hora</p>
            <p className="font-semibold text-zinc-900">{confirmDate} a las {confirmTime}</p>
          </div>
          <p className="text-xs text-zinc-400">
            Para cancelar o modificar tu cita, contacta directamente con el centro.
          </p>
        </div>
      </div>
    );
  }

  // ── Step 2: Client form ──────────────────────────────────────────────────────

  if (step === 2) {
    const localDate = isoToLocalDate(selectedSlot.startDateTime, timezone);
    const slotDate = `${DAYS_ES[localDate.getDay()]} ${localDate.getDate()} de ${MONTHS_ES[localDate.getMonth()]}`;
    const slotTime = selectedSlot.timeLabel;
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-md mx-auto space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-xl font-bold text-zinc-900">{center.name}</h1>
            <p className="text-sm text-zinc-500 mt-1">Reserva tu cita</p>
          </div>

          {/* Summary */}
          <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200 text-sm">
            <p className="font-semibold text-zinc-800">{selectedCalendar?.name}</p>
            <p className="text-zinc-500 mt-0.5">{slotDate} · {slotTime}</p>
          </div>

          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {submitError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.fullName}
                onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-800"
                placeholder="Tu nombre"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Apellidos</label>
              <input
                type="text"
                value={form.surname}
                onChange={e => setForm(f => ({ ...f, surname: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-800"
                placeholder="Tus apellidos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-800"
                placeholder="tu@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Teléfono</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-800"
                placeholder="+34 600 000 000"
              />
            </div>

            <div className="pt-2 flex gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setSubmitError(null); }}
                className="flex-1 border border-zinc-300 text-zinc-700 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-zinc-50 transition-colors"
              >
                Volver
              </button>
              <button
                type="submit"
                disabled={!formValid || submitting}
                className="flex-1 bg-zinc-900 text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Confirmando…
                  </span>
                ) : 'Confirmar reserva'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── Step 1: Calendar + slot selection ───────────────────────────────────────

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-zinc-900">{center.name}</h1>
          <p className="text-sm text-zinc-500 mt-1">Reserva tu cita online</p>
        </div>

        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
            {submitError}
          </div>
        )}

        {/* Calendar selector (if more than 1) */}
        {calendars.length > 1 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-zinc-700">Selecciona el servicio</p>
            <div className="flex flex-wrap gap-2">
              {calendars.map(cal => (
                <button
                  key={cal.id}
                  onClick={() => handleCalendarSelect(cal)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    selectedCalendar?.id === cal.id
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'border-zinc-300 text-zinc-700 hover:border-zinc-500'
                  }`}
                >
                  {cal.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {calendars.length === 0 && (
          <p className="text-center text-zinc-500 py-8 text-sm">
            No hay servicios disponibles para reserva online en este momento.
          </p>
        )}

        {/* Availability grid */}
        {selectedCalendar && (
          <div className="space-y-4">
            {/* Week navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrevWeek}
                disabled={weekStart <= startOfWeekMonday(new Date())}
                className="p-2 rounded-lg border border-zinc-200 hover:border-zinc-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Semana anterior"
              >
                <svg className="w-4 h-4 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-medium text-zinc-700">
                {weekDays[0].getUTCDate()} — {weekDays[6].getUTCDate()} de {MONTHS_ES[weekDays[6].getUTCMonth()]}
              </span>
              <button
                onClick={handleNextWeek}
                className="p-2 rounded-lg border border-zinc-200 hover:border-zinc-400 transition-colors"
                aria-label="Semana siguiente"
              >
                <svg className="w-4 h-4 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {loadingAvail ? <Spinner /> : (
              <>
                {/* Desktop: full week grid */}
                <div className="hidden sm:grid sm:grid-cols-7 gap-2">
                  {weekDays.map((date, i) => {
                    const dayKey = DAY_KEYS[i];
                    const slots = buildSlotsForDay(
                      dayKey,
                      availability?.publicAvailability,
                      availability?.bookings || [],
                      availability?.capacityPerSlot || 1,
                      timezone,
                      date,
                    );
                    const isToday = toUTCDateString(date) === toUTCDateString(new Date());
                    return (
                      <div key={dayKey} className="space-y-1">
                        <div className={`text-center pb-1 border-b border-zinc-100 ${isToday ? 'text-zinc-900' : 'text-zinc-500'}`}>
                          <p className="text-xs font-semibold uppercase">
                            {['L', 'M', 'X', 'J', 'V', 'S', 'D'][i]}
                          </p>
                          <p className={`text-sm font-bold ${isToday ? 'text-zinc-900' : ''}`}>
                            {date.getUTCDate()}
                          </p>
                        </div>
                        {slots.length === 0 ? (
                          <p className="text-center text-zinc-300 text-xs py-2">—</p>
                        ) : slots.map(slot => (
                          <button
                            key={slot.startDateTime}
                            onClick={() => handleSlotSelect(slot)}
                            disabled={!slot.available}
                            className={`w-full text-xs py-1.5 rounded-lg font-medium transition-colors ${
                              selectedSlot?.startDateTime === slot.startDateTime
                                ? 'bg-zinc-900 text-white'
                                : slot.available
                                  ? 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
                                  : 'bg-zinc-50 text-zinc-300 cursor-not-allowed line-through'
                            }`}
                          >
                            {slot.timeLabel}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* Mobile: single day view */}
                <div className="sm:hidden">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => setMobileDayIndex(i => Math.max(0, i - 1))}
                      disabled={mobileDayIndex === 0}
                      className="p-1.5 rounded border border-zinc-200 hover:border-zinc-400 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <div className="text-center">
                      <p className="text-xs text-zinc-500 uppercase font-semibold">
                        {['L', 'M', 'X', 'J', 'V', 'S', 'D'][mobileDayIndex]}
                      </p>
                      <p className="text-sm font-bold text-zinc-900">
                        {formatDateLabel(weekDays[mobileDayIndex])}
                      </p>
                    </div>
                    <button
                      onClick={() => setMobileDayIndex(i => Math.min(6, i + 1))}
                      disabled={mobileDayIndex === 6}
                      className="p-1.5 rounded border border-zinc-200 hover:border-zinc-400 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {(() => {
                    const dayKey = DAY_KEYS[mobileDayIndex];
                    const slots = buildSlotsForDay(
                      dayKey,
                      availability?.publicAvailability,
                      availability?.bookings || [],
                      availability?.capacityPerSlot || 1,
                      timezone,
                      weekDays[mobileDayIndex],
                    );
                    if (slots.length === 0) return (
                      <p className="text-center text-zinc-400 text-sm py-6">No hay horarios disponibles este día</p>
                    );
                    return (
                      <div className="grid grid-cols-3 gap-2">
                        {slots.map(slot => (
                          <button
                            key={slot.startDateTime}
                            onClick={() => handleSlotSelect(slot)}
                            disabled={!slot.available}
                            className={`text-sm py-2.5 rounded-lg font-medium transition-colors ${
                              selectedSlot?.startDateTime === slot.startDateTime
                                ? 'bg-zinc-900 text-white'
                                : slot.available
                                  ? 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
                                  : 'bg-zinc-50 text-zinc-300 cursor-not-allowed line-through'
                            }`}
                          >
                            {slot.timeLabel}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </>
            )}

            {!availability && !loadingAvail && (
              <p className="text-center text-zinc-400 text-sm py-6">
                No hay horarios configurados para este servicio.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
